import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, Eye, EyeOff } from 'lucide-react';
import PinDigits from './PinDigits';
import './agent-pin-enrollment.css';

// These steps collect input only. The existing credentials API validates the
// password and saves the PIN together, on the final explicit submission.
export default function AgentPinEnrollment({ password, setPassword, pin, setPin, repeat, setRepeat,
    recovery, setRecovery, recoveryRequired, changing, busy, error, onSubmit, onExit, onReload, login }) {
    const [step, setStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [inputError, setInputError] = useState('');
    const panel = useRef(null);
    useEffect(() => { if (!busy) panel.current?.querySelector('.pin-enroll-input input')?.focus(); }, [step, busy]);
    const headings = ['로그인 비밀번호를\n입력해주세요', '사용할 PIN을\n설정해주세요', '같은 PIN을\n한 번 더 입력해주세요'];
    const descriptions = ['계정 보안을 위해 먼저 로그인 비밀번호를 입력해주세요.', '본인만 아는 4자리 PIN으로\n계정을 보호하세요.', '입력한 PIN이 맞는지 다시 한번\n확인해주세요.'];
    const back = () => {
        if (busy) return;
        setInputError(''); setShowPassword(false);
        if (!step) { onExit?.(); return; }
        setRepeat('');
        if (step === 1) setPin('');
        setStep(value => value - 1);
    };
    const next = event => {
        event.preventDefault();
        if (busy) return;
        setInputError('');
        if (step === 0) {
            if (!password || (recoveryRequired && !recovery.trim())) return;
            setShowPassword(false); setStep(1);
        } else if (step === 1) {
            if (!/^\d{4}$/.test(pin)) return;
            if (/^(\d)\1{3}$/.test(pin) || '0123456789'.includes(pin) || '9876543210'.includes(pin)) {
                setInputError('반복되거나 연속된 PIN은 사용할 수 없습니다.');
                setPin(''); setRepeat('');
                // Focus synchronously in the submit gesture so mobile browsers
                // can reopen the numeric keyboard without another tap.
                panel.current?.querySelector('.pin-enroll-input input')?.focus();
                return;
            }
            setRepeat(''); setStep(2);
        } else {
            if (!/^\d{4}$/.test(repeat)) return;
            if (pin !== repeat) {
                setInputError('앞에서 입력한 PIN과 다릅니다. 다시 입력해주세요.'); setRepeat('');
                panel.current?.querySelector('.pin-enroll-input input')?.focus();
                return;
            }
            onSubmit(event);
        }
    };
    const incomplete = step === 0 ? !password || (recoveryRequired && !recovery.trim()) : step === 1 ? pin.length !== 4 : repeat.length !== 4;
    return <section className="pin-ui pin-enroll" ref={panel} aria-busy={busy} aria-label={changing ? 'PIN 변경' : 'PIN 등록'}>
        <div className="pin-enroll-top"><button type="button" className="pin-enroll-back" onClick={back} disabled={busy} aria-label={step ? '이전 단계' : login ? '로그인으로 돌아가기' : '닫기'}><ChevronLeft size={22}/></button><strong>{changing ? 'PIN 변경' : 'PIN 등록'}</strong><span aria-live="polite" aria-label={`총 3단계 중 ${step + 1}단계`}>{step + 1} / 3</span></div>
        <ol className="pin-enroll-progress" aria-label="PIN 등록 단계">{['비밀번호 입력', 'PIN 설정', 'PIN 재확인'].map((label, index) => <li key={label} className={index <= step ? 'is-active' : ''} aria-current={index === step ? 'step' : undefined}><span>{label}</span></li>)}</ol>
        <form onSubmit={next} className="pin-enroll-form">
            <h2 className="pin-enroll-title">{headings[step]}</h2>
            <p className="pin-enroll-description">{descriptions[step]}</p>
            <div className="pin-enroll-input" key={step}>
                {step === 0 ? <>
                    <label className="pin-field">현재 로그인 비밀번호<div className="pin-enroll-password"><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} disabled={busy} onChange={e => { setPassword(e.target.value); setPin(''); setRepeat(''); setInputError(''); }}/><button type="button" aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'} aria-pressed={showPassword} disabled={busy} onClick={() => setShowPassword(value => !value)}>{showPassword ? <EyeOff size={20}/> : <Eye size={20}/>}</button></div></label>
                    {recoveryRequired && <label className="pin-field pin-enroll-recovery">관리자에게 전달받은 복구 권한<input type="password" autoComplete="off" value={recovery} disabled={busy} onChange={e => setRecovery(e.target.value)}/></label>}
                </> : <div className="pin-enroll-pin"><PinDigits label={step === 1 ? '새 PIN 4자리' : '새 PIN 확인'} value={step === 1 ? pin : repeat} onChange={value => { if (step === 1) { setPin(value); setRepeat(''); } else setRepeat(value); setInputError(''); }} disabled={busy}/><p className="pin-enroll-help">{step === 1 ? '반복·연속 숫자는 사용할 수 없어요.' : '등록 후 다시 로그인해주세요.'}</p></div>}
            </div>
            {(error || inputError) && <p className="pin-error" role="alert">{inputError || error}</p>}
            <div className="pin-enroll-actions"><button className="pin-primary" type="submit" disabled={busy || incomplete}>{busy ? '저장 중…' : step === 2 ? changing ? 'PIN 변경' : 'PIN 등록' : '다음'}</button>
                {step === 0 && <button type="button" className="pin-enroll-exit" disabled={busy} onClick={onExit}>{login ? '로그인으로 돌아가기' : '닫기'}</button>}
                {error && <button type="button" className="pin-enroll-exit" disabled={busy} onClick={onReload}>설정 다시 조회</button>}
            </div>
        </form>
    </section>;
}
